import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';

type WishlistDialogProps = {
  open: boolean;
  onClose: () => void;
  username: string | null;
};

const WishlistDialog: React.FC<WishlistDialogProps> = ({ open, onClose, username }) => {
  const [wishlistItems, setWishlistItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchWishlist = async () => {
      if (!username) return;

      try {
        const response = await fetch(
          `https://shop.petsync.in/wp-json/yithwishlist/v1/get?secret_key=83fef8ec35f37968a9b684a5c400a54a&username=${username}`
        );
        const data = await response.json();
        setWishlistItems(data?.wishlist || []);
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setLoading(true);
      fetchWishlist();
    }
  }, [username, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: '#f8f8f8', // slightly off-white for contrast
          color: '#000000',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          py: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Wishlist of {username}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          px: 4,
          py: 3,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: 3,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 3 },
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" width="100%" py={10}>
            <CircularProgress sx={{ color: '#000000' }} />
          </Box>
        ) : wishlistItems.length > 0 ? (
          wishlistItems.map((item, idx) => (
            <motion.div
              key={item.product_id || idx}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, type: 'spring', stiffness: 120 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #000000',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.04)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.images[0]}
                  alt={item.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />

                {/* Overlay on hover */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.05)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '&:hover': { opacity: 1 },
                  }}
                />

                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Link href={item.permalink} target="_blank" underline="hover" sx={{ color: '#000000' }}>
                      {item.name}
                    </Link>
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ₹ {item.price}
                  </Typography>

                  {/* Quick Action Button */}
                  <Button
                    href={item.permalink}
                    target="_blank"
                    variant="outlined"
                    sx={{
                      mt: 1,
                      width: '100%',
                      borderColor: '#000000',
                      color: '#000000',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': { background: '#000000', color: '#ffffff' },
                    }}
                  >
                    View Product
                  </Button>
                </Box>
              </Box>
            </motion.div>
          ))
        ) : (
          <Typography align="center" sx={{ py: 10, color: '#555555', gridColumn: '1/-1' }}>
            No items found in the wishlist.
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 4,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: '#000000',
            color: '#ffffff',
            px: 5,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            '&:hover': { background: '#333333' },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default WishlistDialog;
